import { useState } from 'react';
import { downloadOCELFile } from '../../../services/api/ocel.api';

function formatRelative(isoString) {
  if (!isoString) return null;
  const ms = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function formatBytes(bytes) {
  if (!bytes) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function DownloadButton({ isDownloading, onDownload }) {
  return (
    <button
      onClick={onDownload}
      disabled={isDownloading}
      className="flex-shrink-0 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
      title="Download file"
    >
      {isDownloading ? (
        <span className="w-3.5 h-3.5 border border-current border-t-transparent rounded-full animate-spin inline-block" />
      ) : (
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
        </svg>
      )}
    </button>
  );
}

function ETLFileCard({ file, isActive, isActivating, isDownloading, onActivate, onDownload }) {
  return (
    <div
      className={`group relative rounded-xl border transition-all ${
        isActive
          ? 'border-primary-400 bg-primary-50 shadow-sm'
          : 'border-gray-200 bg-white hover:border-primary-200 hover:shadow-sm'
      }`}
    >
      <div className="px-4 py-3 flex items-start gap-3">
        {/* ETL badge */}
        <div className="mt-0.5 flex-shrink-0">
          <span className="text-xs font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">ETL</span>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-800 font-mono truncate">
              {file.filename}
            </span>
            {isActive && (
              <span className="flex-shrink-0 text-xs font-medium px-1.5 py-0.5 rounded-full bg-primary-100 text-primary-700">
                Active
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1">
            {file.size_bytes && (
              <span className="text-xs text-gray-400">{formatBytes(file.size_bytes)}</span>
            )}
            {file.created_at && (
              <span className="text-xs text-gray-400">{formatRelative(file.created_at)}</span>
            )}
          </div>
        </div>

        <DownloadButton isDownloading={isDownloading} onDownload={() => onDownload(file.file_type, file.filename)} />

        <button
          onClick={() => onActivate(file.file_type)}
          disabled={isActive || isActivating}
          className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
            isActive
              ? 'bg-primary-100 text-primary-600 cursor-default'
              : 'bg-gray-100 hover:bg-primary-600 hover:text-white text-gray-600'
          } disabled:opacity-50`}
        >
          {isActivating ? (
            <span className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
          ) : isActive ? (
            <>
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
              </svg>
              Using
            </>
          ) : (
            'Open Project'
          )}
        </button>
      </div>
    </div>
  );
}

function BuiltInFileCard({ file, isActive, isActivating, isDownloading, onActivate, onDownload }) {
  const displayName = file.display_name ?? file.file_type;
  const relativeDate = formatRelative(file.upload_date);

  return (
    <div
      className={`group rounded-xl border transition-all ${
        isActive
          ? 'border-primary-400 bg-primary-50'
          : 'border-gray-200 bg-white hover:border-gray-300'
      }`}
    >
      <div className="px-4 py-3 flex items-start gap-3">
        <div className="mt-0.5 flex-shrink-0">
          <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${
            file.is_user_upload ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'
          }`}>
            {file.is_user_upload ? 'UPL' : 'SYS'}
          </span>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700 truncate">{displayName}</span>
            {isActive && (
              <span className="flex-shrink-0 text-xs font-medium px-1.5 py-0.5 rounded-full bg-primary-100 text-primary-700">
                Active
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <span className="truncate">{file.file_type}</span>
            {relativeDate && <span className="flex-shrink-0">&middot; {relativeDate}</span>}
          </div>
        </div>

        <DownloadButton isDownloading={isDownloading} onDownload={() => onDownload(file.file_type, file.display_name)} />

        <button
          onClick={() => onActivate(file.file_type)}
          disabled={isActive || isActivating}
          className={`flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${
            isActive
              ? 'bg-primary-100 text-primary-600 cursor-default'
              : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
          } disabled:opacity-50`}
        >
          {isActivating ? (
            <span className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin inline-block" />
          ) : isActive ? 'Using' : 'Open Project'}
        </button>
      </div>
    </div>
  );
}

function compareFiles(a, b, sortKey, sortDir) {
  const dir = sortDir === 'asc' ? 1 : -1;
  if (sortKey === 'name') {
    const nameA = a.filename ?? a.display_name ?? '';
    const nameB = b.filename ?? b.display_name ?? '';
    return dir * nameA.localeCompare(nameB);
  }
  if (sortKey === 'size') {
    const sizeA = a.size_bytes ?? a.file_size ?? 0;
    const sizeB = b.size_bytes ?? b.file_size ?? 0;
    return dir * (sizeA - sizeB);
  }
  const dateA = new Date(a.created_at ?? a.upload_date ?? 0).getTime();
  const dateB = new Date(b.created_at ?? b.upload_date ?? 0).getTime();
  return dir * (dateA - dateB);
}

function SectionHeader({ title, count, action }) {
  return (
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
        {count != null && (
          <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">{count}</span>
        )}
      </div>
      {action}
    </div>
  );
}

export default function DataSourcesList({
  etlFiles,
  builtInFiles,
  activeFileType,
  activatingFile,
  refreshingFiles,
  etlServiceReachable,
  onActivate,
  onRefresh,
}) {
  const [sortKey, setSortKey] = useState('date'); // 'name' | 'date' | 'size'
  const [sortDir, setSortDir] = useState('desc'); // 'asc' | 'desc'
  const [downloadingFile, setDownloadingFile] = useState(null);

  const sortedEtlFiles = [...etlFiles].sort((a, b) => compareFiles(a, b, sortKey, sortDir));
  const sortedBuiltInFiles = [...builtInFiles].sort((a, b) => compareFiles(a, b, sortKey, sortDir));

  const handleDownload = async (fileType, displayName) => {
    setDownloadingFile(fileType);
    try {
      await downloadOCELFile(fileType, displayName);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloadingFile(null);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-base font-semibold text-gray-800">Available Datasets</h2>
          <p className="text-xs text-gray-400 mt-0.5">Select a dataset to use for analysis</p>
        </div>
        <div className="flex items-center gap-1.5">
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="date">Sort by date</option>
            <option value="name">Sort by name</option>
            <option value="size">Sort by size</option>
          </select>
          <button
            onClick={() => setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))}
            className="p-1.5 text-xs text-gray-500 hover:text-gray-700 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
            title={sortDir === 'asc' ? 'Ascending' : 'Descending'}
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7.5 8 19.5m0 0-3-3m3 3 3-3M16 16.5 16 4.5m0 0 3 3m-3-3-3 3" />
            </svg>
          </button>
          <button
            onClick={onRefresh}
            disabled={refreshingFiles}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 px-2.5 py-1.5 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors disabled:opacity-50"
            title="Scan for new ETL-generated files"
          >
            <svg
              className={`w-3.5 h-3.5 ${refreshingFiles ? 'animate-spin' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* ETL-generated files */}
      <div className="mb-6">
        <SectionHeader
          title="Automated ETL"
          count={etlFiles.length}
          action={
            !etlServiceReachable && (
              <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                ETL service offline
              </span>
            )
          }
        />

        {etlFiles.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-gray-200 px-4 py-8 text-center">
            <div className="text-gray-300 text-3xl mb-2">⚙</div>
            <p className="text-sm text-gray-500 font-medium">No automated OCEL files yet</p>
            <p className="text-xs text-gray-400 mt-1">
              {etlServiceReachable
                ? 'Trigger a manual run to generate the first file.'
                : 'Start the ETL service to begin automated extraction.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {sortedEtlFiles.map(file => (
              <ETLFileCard
                key={file.file_type}
                file={file}
                isActive={activeFileType === file.file_type}
                isActivating={activatingFile === file.file_type}
                isDownloading={downloadingFile === file.file_type}
                onActivate={onActivate}
                onDownload={handleDownload}
              />
            ))}
          </div>
        )}
      </div>

      {/* Built-in & uploaded files */}
      {builtInFiles.length > 0 && (
        <div>
          <SectionHeader title="Built-in & Uploaded" count={builtInFiles.length} />
          <div className="space-y-2">
            {sortedBuiltInFiles.map(file => (
              <BuiltInFileCard
                key={file.file_type}
                file={file}
                isActive={activeFileType === file.file_type}
                isActivating={activatingFile === file.file_type}
                isDownloading={downloadingFile === file.file_type}
                onActivate={onActivate}
                onDownload={handleDownload}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
